import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { DownloadUploadService } from '@thehubz/client/asset-services';
import {
  AssetUIModel,
  AssetsGenerateUrlService,
  ClientCoreModule,
  ClientNotificationHandlerService,
  StorageUtilityLib,
  USER_SESSION_TOKEN,
  UserSession,
} from '@thehubz/client/core';
import { AssetsApiService, CreateAssetDto } from '@thehubz/client/data-access-api';
import { AnyType } from '@thehubz/shared/model';
import { lastValueFrom } from 'rxjs';
import { AssetFullScreen } from '../../full-screen/asset-full-screen';
import { AnnotationsPdfService } from '../../services/annotations-pdf.service';
import { AssetAnnotatorService } from '../../services/asset-annotator.service';
import { TmpAssetStorageService } from '../../services/tmp-asset-storage.service';
import { AnnotationCommentsComponent } from '../annotation-comments/annotation-comments.component';
import { AssetViewComponent } from '../asset-detail-view/asset-info/components/asset-view/asset-view.component';
import { ImageEditorComponent } from '../image-editor/image-editor.component';
import { PdfEditorComponent } from '../pdf-editor/pdf-editor.component';
import { VideoEditorComponent } from '../video-editor/video-editor.component';

@Component({
  selector: 'thehubz-asset-annotator',
  templateUrl: './asset-annotator.component.html',
  styleUrls: ['./asset-annotator.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    ClientCoreModule,
    AssetViewComponent,
    ImageEditorComponent,
    VideoEditorComponent,
    PdfEditorComponent,
    AnnotationCommentsComponent,
  ],
})
export class AssetAnnotatorComponent implements OnInit {
  @Input() assetId = '';
  @Input() readOnly = false;
  @Input() sharedFlag = false;
  @Input() version!: number;
  @Input() assetInfo?: AnyType;
  @Output() assetEdited: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('imageEditor') imageEditorComp?: ImageEditorComponent;
  @ViewChild('videoEditor') videoEditorComp?: VideoEditorComponent;
  @ViewChild('videoContainer', { read: ElementRef }) videoContainer!: ElementRef;
  @ViewChild('pdfEditor') pdfEditorComp?: PdfEditorComponent;
  @ViewChild('commentsArea') commentsComp?: AnnotationCommentsComponent;

  asset!: AssetUIModel;
  fileUrl = '';
  currentUser?: UserSession;
  selectedAnnotation: any = null;
  selectedAnnotationIndex = -1;
  annotationDrawn = false;
  isDeleteAnnotation = false;
  selectedReplyIndex = -1;
  assetTypes = CreateAssetDto.AssetTypeEnum;
  isSupported = true;
  currentPage = -1;

  constructor(
    @Inject(USER_SESSION_TOKEN) private userSessionToken: string,
    public assetAnnotatorService: AssetAnnotatorService,
    private assetService: AssetsApiService,
    private assetGenerateUrlService: AssetsGenerateUrlService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private storageUtility: StorageUtilityLib,
    private assetFullScreen: AssetFullScreen,
    private downloadUploadService: DownloadUploadService,
    private tmpAssetStorage: TmpAssetStorageService,
    private annotationsPdfService: AnnotationsPdfService,
  ) {}

  ngOnInit(): void {
    this.currentUser = this.storageUtility.getLocalStorage<UserSession>(this.userSessionToken);
    if (this.assetId) {
      if (this.sharedFlag) {
        this.setAssetDetails(this.assetInfo);
      } else {
        this.assetService.assetsControllerFindOne(this.assetId).subscribe(async (asset) => {
          this.setAssetDetails(asset);
        });
      }
    }
    this.downloadUploadService.emitDownloadAsset('showDownload');
    this.annotationsPdfService.$getCurrentPage().subscribe((page) => {
      console.log('Page update', page);
      this.currentPage = page;
    });
  }

  async setAssetDetails(asset: AnyType) {
    let assetVersion: AnyType;
    if (this.version) {
      assetVersion = asset.versions.find((version: AnyType) => version.version === this.version);
    } else {
      assetVersion = asset.currentVersion ? asset.currentVersion : undefined;
    }
    this.asset = this.assetGenerateUrlService.addPreviewUrl(asset);
    if (this.asset) {
      this.isSupported = this.assetAnnotatorService.isSupportedForAnnotation(this.asset?.assetType);
      if (!this.isSupported) {
        //setting the value to true because for unsupported asset type we dont need to validate annotations
        this.annotationDrawn = true;
      }
      this.fileUrl =
        this.asset.assetType === this.assetTypes.Pdf
          ? await this.assetGenerateUrlService.getMainPresignedUrl(this.asset, undefined, this.version)
          : await this.assetGenerateUrlService.getResizedPresignedUrl(this.asset, 'gen-preview-xl.webp', this.version);

      let assetVersionInfo: AnyType = {};
      if (this.sharedFlag) {
        assetVersionInfo = asset.currentVersion;
      } else {
        assetVersionInfo = assetVersion ? await lastValueFrom(this.assetService.assetsControllerGetAssetVersion(assetVersion?._id)) : undefined;
      }

      if (assetVersionInfo?.annotations) {
        this.assetAnnotatorService.stageJSONData = assetVersionInfo.annotations;
        this.assetAnnotatorService.annotations = [...this.assetAnnotatorService.stageJSONData.children[0].children];
      } else {
        this.assetAnnotatorService.stageJSONData = null;
        this.assetAnnotatorService.annotations = [];
      }
      this.tmpAssetStorage.updateAsset(this.asset);
    }
  }

  drawToolClick(tool: string) {
    //remove any unsaved annotation
    this.assetAnnotatorService.removeUnsavedAnnotations();
    this.selectedAnnotation = null;
    if (this.commentsComp) {
      this.commentsComp.newComment = '';
      this.commentsComp.isReplying = false;
    }

    if (this.asset && this.asset.assetType == this.assetTypes.Image && this.imageEditorComp) this.imageEditorComp.draw(tool);
    else if (this.asset && this.asset.assetType == this.assetTypes.Video && this.videoEditorComp) this.videoEditorComp.draw(tool);
    else if (this.asset && this.asset.assetType == this.assetTypes.Pdf && this.pdfEditorComp) this.pdfEditorComp.draw(tool);

    this.annotationDrawn = true;
  }

  setColor(colorCode: string) {
    this.assetAnnotatorService.color = colorCode;
    if (this.annotationDrawn) {
      this.assetAnnotatorService.changeCurrentNodeColor(colorCode);
    }
  }

  setStroke(size: number) {
    this.assetAnnotatorService.strokeWidth = size;
    if (this.annotationDrawn) {
      this.assetAnnotatorService.changeCurrentNodeStroke(size);
    }
  }

  toggleAnnotation(e: { annotation: any; index: number }) {
    this.assetAnnotatorService.annotations[e.index].attrs.visible = this.assetAnnotatorService.toggleAnnotationVisibility(e.annotation);
  }

  onEditClick(e: { annotation: any; index: number }) {
    if (this.isSupported) {
      this.editAnnotation(e.annotation, e.index);
    } else {
      this.selectedAnnotation = e.annotation;
      this.selectedAnnotationIndex = e.index;
    }
  }

  editAnnotation(annotation: any, index: number) {
    //if user is not viewing the annotation on screen than first we need to navigate to that particular annotation
    let waitForPDFRender = false;
    if (this.asset && this.asset.assetType == this.assetTypes.Pdf) {
      if (this.pdfEditorComp && this.pdfEditorComp.pdfCurrentPage != annotation.attrs.page) {
        waitForPDFRender = true;
        this.pdfEditorComp.gotoPage(annotation.attrs.page);
      }
    } else if (this.asset && this.asset.assetType == this.assetTypes.Video) {
      if (this.videoEditorComp && this.videoEditorComp.pausedTime != annotation.attrs.timestamp) this.videoEditorComp.gotoTimeframe(annotation.attrs.timestamp);
    }
    setTimeout(
      () => {
        this.assetAnnotatorService.detachAllTransformers();
        this.selectedAnnotation = this.assetAnnotatorService.makeAnnotationEditable(annotation);
        this.selectedAnnotationIndex = index;
        this.annotationDrawn = true;
      },
      waitForPDFRender ? 1000 : 0,
    );
  }

  onDeleteClick(e: { annotation: any; index: number }) {
    if (this.isSupported) {
      this.deleteAnnotation(e.annotation, e.index);
    } else {
      this.isDeleteAnnotation = true;
      this.selectedAnnotationIndex = e.index;
      this.saveCommentsForUnsupportedAssetType('', false, false, false);
    }
  }

  deleteAnnotation(annotation: any, index: number) {
    //if user is not viewing the annotation on screen than first we need to navigate to that particular annotation
    if (this.asset && this.asset.assetType == this.assetTypes.Pdf) {
      if (this.pdfEditorComp && this.pdfEditorComp.pdfCurrentPage != annotation.attrs.page) this.pdfEditorComp.gotoPage(annotation.attrs.page);
    } else if (this.asset && this.asset.assetType == this.assetTypes.Video) {
      if (this.videoEditorComp?.pausedTime != annotation.attrs.timestamp) this.videoEditorComp?.gotoTimeframe(annotation.attrs.timestamp);
    }

    this.isDeleteAnnotation = true;
    this.assetAnnotatorService.deleteAnnotation(annotation);
    this.selectedAnnotationIndex = index;
    this.saveAnnotations('', false, false, false);
  }

  annotationTimeClick(e: number) {
    this.videoEditorComp?.gotoTimeframe(e);
  }

  annotationPageClick(e: number) {
    this.pdfEditorComp?.gotoPage(e);
  }

  replyToAnnotation(e: { annotation: any; index: number }) {
    this.selectedAnnotation = null;
    this.selectedAnnotationIndex = e.index;
  }

  editReply(e: { annotationIndex: number; reply: any; index: number }) {
    this.selectedAnnotationIndex = e.annotationIndex;
    this.selectedReplyIndex = e.index;
  }

  deleteReply(e: { annotationIndex: number; index: number }) {
    this.selectedAnnotationIndex = e.annotationIndex;
    this.selectedReplyIndex = e.index;
    if (this.isSupported) this.saveAnnotations('', true, false, true);
    else this.saveCommentsForUnsupportedAssetType('', true, false, true);
  }

  replyToReply(e: { annotationIndex: number }) {
    this.selectedAnnotationIndex = e.annotationIndex;
  }

  onSaveClick(e: any) {
    if (this.isSupported) this.saveAnnotations(e.comment, e.isReply, e.isReplyEdit, e.isReplyDelete);
    else this.saveCommentsForUnsupportedAssetType(e.comment, e.isReply, e.isReplyEdit, e.isReplyDelete);
  }

  async saveAnnotations(newComment: string, isReplying: boolean, isEditReply: boolean, isDeleteReply: boolean) {
    const updatedAt = new Date().toISOString();
    const author: any = this.currentUser?.userData;
    if (author) {
      author.id = author._id;
      author.name = author.firstName + ' ' + author.lastName;
    }
    if (this.isDeleteAnnotation) {
      //deleting an existing annotation
      this.assetAnnotatorService.annotations.splice(this.selectedAnnotationIndex, 1);
    } else if (this.selectedAnnotation) {
      //updating an existing annotation
      this.assetAnnotatorService.updateAnnotationComment(this.selectedAnnotation, newComment, updatedAt);
      this.assetAnnotatorService.annotations[this.selectedAnnotationIndex] = this.selectedAnnotation.toObject();
    } else if (isReplying) {
      //since adding a reply doesn't change anything on canvas...hence directly updating the json
      if (isDeleteReply) {
        this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies.splice(this.selectedReplyIndex, 1);
      } else if (isEditReply) {
        this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies[this.selectedReplyIndex].comment = newComment;
        this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies[this.selectedReplyIndex].updatedAt = updatedAt;
      } else {
        if (!this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies)
          this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies = [];
        const replyObj = {
          comment: newComment,
          updatedAt: updatedAt,
          createdBy: author,
        };
        this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies.push(replyObj);
      }
    } else {
      //inserting new annotation
      const data = this.assetAnnotatorService.addAnnotationComment(newComment, author, updatedAt, this.annotationDrawn);
      console.log('data.annotation', data.annotation, this.currentPage);
      if (typeof data.annotation.attrs !== 'undefined' && typeof data.annotation.attrs.page === 'undefined' && this.currentPage > 0) {
        data.annotation.attrs.page = this.currentPage;
      }
      this.assetAnnotatorService.annotations.push(data.annotation);
      if (data.transformer) this.assetAnnotatorService.annotations.push(data.transformer);
    }

    //reset zoom of konva canvas before saving
    /* Disable reset on save due to ticket 863h51vzy
    if (this.asset && this.asset.assetType == this.assetTypes.Image) {
      this.imageEditorComp?.resetZoom();
    } else if (this.asset && this.asset.assetType == this.assetTypes.Pdf) {
      this.pdfEditorComp?.resetZoom();
    }
    */
    this.assetAnnotatorService.stageJSONData = this.assetAnnotatorService.konvaStage.toObject();
    this.assetAnnotatorService.stageJSONData.children[0].children = [...this.assetAnnotatorService.annotations];
    this.assetAnnotatorService.annotations = [...this.assetAnnotatorService.annotations];

    //resetting the konvajs stage back to its original dimensions before saving to DB
    this.assetAnnotatorService.resetCanvasToOriginalSize();

    if (this.asset?.currentVersion) {
      this.asset.currentVersion.annotations = this.assetAnnotatorService.stageJSONData;
      this.asset.currentVersion.annotationsCount = this.assetAnnotatorService.annotations.filter(
        (ele: any) => ele.className !== 'Transformer' && ele.attrs.comment,
      ).length;

      this.assetService
        .assetsControllerUpdateAnnotations(this.assetId, {
          annotations: this.asset.currentVersion.annotations,
          annotationsCount: this.asset.currentVersion.annotationsCount,
        })
        .subscribe((r: any) => {
          this.setAssetDetails(r);
          this.showSuccessMessage(isReplying, isEditReply, isDeleteReply);
        });
    }
  }

  showSuccessMessage(isReplying: boolean, isEditReply: boolean, isDeleteReply: boolean) {
    if (this.isDeleteAnnotation) {
      this.isDeleteAnnotation = false;
      this.clientNotificationHandlerService.successNotification($localize`Annotation deleted successfully`);
    } else if (this.selectedAnnotation) {
      this.selectedAnnotation = null;
      this.clientNotificationHandlerService.successNotification($localize`Annotation updated successfully`);
    } else if (isReplying && this.commentsComp) {
      this.commentsComp.isReplying = false;
      if (isDeleteReply) {
        this.commentsComp.isDeleteReply = false;
        this.clientNotificationHandlerService.successNotification($localize`Reply deleted successfully`);
      } else if (isEditReply) {
        this.commentsComp.isEditReply = false;
        this.clientNotificationHandlerService.successNotification($localize`Reply updated successfully`);
      } else {
        this.clientNotificationHandlerService.successNotification($localize`Reply added successfully`);
      }
    } else {
      this.clientNotificationHandlerService.successNotification($localize`Annotation saved successfully`);
    }

    if (this.commentsComp) this.commentsComp.newComment = '';
    this.annotationDrawn = false;
    this.assetAnnotatorService.detachAllTransformers();
    this.assetEdited.emit(true);
  }

  saveCommentsForUnsupportedAssetType(newComment: string, isReplying: boolean, isEditReply: boolean, isDeleteReply: boolean) {
    const updatedAt = new Date().toISOString();
    const author: any = this.currentUser?.userData;
    author.id = author._id;
    author.name = author.firstName + ' ' + author.lastName;

    if (this.isDeleteAnnotation) {
      //deleting an existing annotation
      this.assetAnnotatorService.annotations.splice(this.selectedAnnotationIndex, 1);
    } else if (this.selectedAnnotation) {
      //updating an existing annotation
      this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.comment = newComment;
      this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.updatedAt = updatedAt;
    } else if (isReplying) {
      //since adding a reply doesn't change anything on canvas...hence directly updating the json
      if (isDeleteReply) {
        this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies.splice(this.selectedReplyIndex, 1);
      } else if (isEditReply) {
        this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies[this.selectedReplyIndex].comment = newComment;
        this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies[this.selectedReplyIndex].updatedAt = updatedAt;
      } else {
        if (!this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies)
          this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies = [];

        const replyObj = {
          comment: newComment,
          updatedAt: updatedAt,
          createdBy: author,
        };
        this.assetAnnotatorService.annotations[this.selectedAnnotationIndex].attrs.replies.push(replyObj);
      }
    } else {
      //inserting new comment
      const data = {
        className: 'Comment',
        attrs: {
          comment: newComment,
          createdBy: author,
          updatedAt: updatedAt,
        },
      };
      this.assetAnnotatorService.annotations.push(data);
    }

    //creating an empty stage json data for unsupported asset type
    if (!this.assetAnnotatorService.stageJSONData) {
      this.assetAnnotatorService.stageJSONData = {
        children: [
          {
            children: [...this.assetAnnotatorService.annotations],
          },
        ],
      };
    } else {
      this.assetAnnotatorService.stageJSONData.children[0].children = [...this.assetAnnotatorService.annotations];
    }

    this.assetAnnotatorService.annotations = [...this.assetAnnotatorService.annotations];
    if (this.asset && this.asset.currentVersion) {
      this.asset.currentVersion.annotations = this.assetAnnotatorService.stageJSONData;
      this.asset.currentVersion.annotationsCount = this.assetAnnotatorService.annotations.filter(
        (ele: any) => ele.className !== 'Transformer' && ele.attrs.comment,
      ).length;

      this.assetService
        .assetsControllerUpdateVersion(this.assetId, {
          annotations: this.asset.currentVersion.annotations,
          annotationsCount: this.asset.currentVersion.annotationsCount,
        })
        .subscribe(() => {
          if (this.isDeleteAnnotation) {
            this.isDeleteAnnotation = false;
            this.clientNotificationHandlerService.successNotification($localize`Comment deleted successfully`);
          } else if (this.selectedAnnotation) {
            this.selectedAnnotation = null;
            this.clientNotificationHandlerService.successNotification($localize`Comment updated successfully`);
          } else if (isReplying && this.commentsComp) {
            this.commentsComp.isReplying = false;
            if (isDeleteReply) {
              this.commentsComp.isDeleteReply = false;
              this.clientNotificationHandlerService.successNotification($localize`Reply deleted successfully`);
            } else if (isEditReply) {
              this.commentsComp.isEditReply = false;
              this.clientNotificationHandlerService.successNotification($localize`Reply updated successfully`);
            } else {
              this.clientNotificationHandlerService.successNotification($localize`Reply added successfully`);
            }
          } else {
            this.clientNotificationHandlerService.successNotification($localize`Comment saved successfully`);
          }

          if (this.commentsComp) this.commentsComp.newComment = '';
          this.assetEdited.emit(true);
        });
    }
  }

  openAssetFullscreen() {
    this.assetFullScreen.openAsset(this.asset, this.version);
  }

  openVideoFullscreen() {
    this.assetFullScreen.openVideo(this.videoContainer);
  }
}
